import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    getRequest,
    postRequest,
    deleteRequest,
    ResponseDataType,
} from 'api';
import {
    Card,
    Row,
    Col,
    Table,
    Button,
    Modal,
    Input,
    List,
    Space,
    Pagination,
} from 'antd';
import { toast } from 'react-toastify';
import { ColumnsType } from 'antd/es/table';

interface ClassYearData {
    classYearId: number;
    className: string;
    classLevel: string;
    schoolName: string;
    schoolYear: string;
}

interface StudentData {
    studentId: number;
    name: string;
    dob: string;
    gender: number;
    studentCode: string;
    cmnd: string;
}
interface ParentData {
    name: string;
    phoneNumber: string;
    email: string;
    cmnd: string;
}

export const HomeroomClassDetail = () => {
    const { classYearId } = useParams<{ classYearId: string }>();

    const [parentModalVisible, setParentModalVisible] = useState(false);
    const [parents, setParents] = useState<ParentData[]>([]);
    const [loadingParents, setLoadingParents] = useState(false);

    const [classInfo, setClassInfo] = useState<ClassYearData | null>(null);
    const [students, setStudents] = useState<StudentData[]>([]);
    const [searchResults, setSearchResults] = useState<StudentData[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [loadingSearch, setLoadingSearch] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);

    useEffect(() => {
        if (classYearId) {
            fetchClassInfo(classYearId);
            fetchStudents(classYearId);
        }
    }, [classYearId]);
    const fetchParentsByStudent = async (studentId: number) => {
        setLoadingParents(true);
        const response: ResponseDataType<ParentData[]> = await getRequest(
            `/cms/student/get-parent-by-student?studentId=${studentId}`
        );

        if (response?.code === 200 && Array.isArray(response.info)) {
            setParents(response.info);
            setParentModalVisible(true);
        } else {
            setParents([]);
            toast.error('Không thể tải danh sách phụ huynh');
        }
        setLoadingParents(false);
    };

    const fetchClassInfo = async (id: string) => {
        const response: ResponseDataType<ClassYearData> = await getRequest(`/cms/class/get-class-year-info?classYearId=${id}`);
        if (response?.code === 200 && response.info) {
            setClassInfo(response.info);
        } else {
            toast.error('Không thể tải thông tin lớp học');
        }
    };

    const fetchStudents = async (id: string) => {
        const res: ResponseDataType<{ list: StudentData[] }> = await getRequest(
            `/cms/student/get-students-by-class-year?classYearId=${id}`
        );
        if (res?.code === 200 && res.info !== undefined && Array.isArray(res.info.list)) {
            setStudents(res.info.list);
        }
        else {
            toast.error('Không thể tải danh sách học sinh');
            setStudents([]);
        }
    };

    const handleSearchByCCCD = async (cmnd: string) => {
        if (!cmnd || cmnd.length < 3) {
            toast.warning('Vui lòng nhập CCCD hợp lệ');
            return;
        }

        setLoadingSearch(true);
        const res: ResponseDataType<StudentData> = await getRequest(
            `/cms/student/get-student-by-cmnd?cmnd=${cmnd}`
        );
        if (res?.code === 200 && res.info) setSearchResults([res.info]);
        else {
            setSearchResults([]);
            toast.error(res?.msg || 'Không tìm thấy học sinh');
        }
        setLoadingSearch(false);
    };

    const handleAddStudent = async (studentId: number) => {
        const res = await postRequest(
            `/cms/class/${classYearId}/add-student-to-class-year`,
            { studentId }
        );
        if (res?.code === 200) {
            toast.success('Thêm học sinh thành công');
            setShowModal(false);
            fetchStudents(classYearId!);
        } else toast.error(res?.msg || 'Thêm học sinh thất bại');
    };

    const handleDeleteStudent = async (studentId: number) => {
        const res = await deleteRequest(
            `/cms/class/${classYearId}/delete-studentClassYear`,
            { studentId }
        );
        if (res?.code === 200) {
            toast.success('Xoá học sinh thành công');
            fetchStudents(classYearId!);
        } else toast.error(res?.msg || 'Xoá thất bại');
    };
    const columns: ColumnsType<StudentData> = [
        {
            title: 'Tên học sinh',
            dataIndex: 'name',
            key: 'name',
            align: 'center' as const,
        },
        {
            title: 'Ngày sinh',
            dataIndex: 'dob',
            key: 'dob',
            align: 'center' as const,
        },
        {
            title: 'Giới tính',
            dataIndex: 'gender',
            key: 'gender',
            align: 'center' as const,
            render: (gender: number) => (gender === 1 ? 'Nam' : 'Nữ'),
        },
        {
            title: 'Mã học sinh',
            dataIndex: 'studentCode',
            key: 'studentCode',
            align: 'center' as const,
        },
        {
            title: 'CCCD',
            dataIndex: 'cmnd',
            key: 'cmnd',
            align: 'center' as const,
        },
        {
            title: 'Hành động',
            key: 'action',
            align: 'center' as const,
            render: (_: any, record: StudentData) => (
                <Space>
                    <Button danger onClick={() => {
                        Modal.confirm({
                            title: 'Bạn có chắc chắn muốn xoá học sinh này?',
                            okText: 'Xoá',
                            okType: 'danger',
                            cancelText: 'Hủy',
                            onOk: () => handleDeleteStudent(record.studentId),
                        });
                    }}>Xoá</Button>

                    <Button type="link" onClick={() => fetchParentsByStudent(record.studentId)}
                        style={{
                            border: '1px solid #1890ff',
                            color: '#1890ff',
                            borderRadius: 4,
                            background: 'white',
                        }}
                    >
                        Thông tin phụ huynh
                    </Button>
                    <Modal
                        title="Thông tin phụ huynh"
                        open={parentModalVisible}
                        onCancel={() => setParentModalVisible(false)}
                        footer={null}
                    >
                        {loadingParents ? (
                            <p>Đang tải thông tin...</p>
                        ) : parents.length > 0 ? (
                            <List
                                style={{ marginTop: 16 }}
                                dataSource={parents}
                                bordered
                                renderItem={(item) => (
                                    <List.Item style={{ border: '1px solid #1890ff', borderRadius: 8, padding: 16 }}>
                                        <div>
                                            <p><strong>Họ tên:</strong> {item.name}</p>
                                            <p><strong>SĐT:</strong> {item.phoneNumber}</p>
                                            <p><strong>Email:</strong> {item.email}</p>

                                        </div>
                                    </List.Item>
                                )}
                            />

                        ) : (
                            <p>Không có phụ huynh nào.</p>
                        )}
                    </Modal>

                </Space>
            ),
        },
    ];

    return (
        <div>
            <Card style={{ marginBottom: 24 }}>
                {classInfo ? (
                    <Row gutter={[16, 16]}>
                        <Col span={12}><strong>Lớp:</strong> {classInfo.className}</Col>
                        <Col span={12}><strong>Khối:</strong> {classInfo.classLevel}</Col>
                        <Col span={12}><strong>Năm học:</strong> {classInfo.schoolYear}</Col>
                        <Col span={12}><strong>Trường:</strong> {classInfo.schoolName}</Col>
                    </Row>
                ) : (
                    <div>Đang tải thông tin lớp...</div>
                )}
            </Card>

            <div style={{ marginBottom: 16 }}>
                <Button type="primary" onClick={() => setShowModal(true)}>
                    Thêm học sinh
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={students.slice((currentPage - 1) * pageSize, currentPage * pageSize)}
                rowKey="studentId"
                pagination={false}
                locale={{ emptyText: 'Không có học sinh' }}
            />

            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <Pagination
                    current={currentPage}
                    pageSize={pageSize}
                    total={students.length}
                    showSizeChanger
                    pageSizeOptions={['5', '10', '20']}
                    onChange={(page, size) => {
                        setCurrentPage(page);
                        setPageSize(size);
                    }}
                />
            </div>

            <Modal
                title="Thêm học sinh theo CCCD"
                open={showModal}
                onCancel={() => setShowModal(false)}
                footer={null}
            >
                <Input.Search
                    placeholder="Nhập CCCD học sinh"
                    enterButton="Tìm kiếm"
                    loading={loadingSearch}
                    onSearch={handleSearchByCCCD}
                />
                <List
                    style={{ marginTop: 16 }}
                    dataSource={searchResults}
                    bordered
                    renderItem={(item) => (
                        <List.Item
                            actions={[
                                <Button type="link" onClick={() => handleAddStudent(item.studentId)}>
                                    Thêm
                                </Button>,
                            ]}
                        >
                            <Space direction="vertical">
                                <div>
                                    <b>{item.name}</b> ({item.studentCode})
                                </div>
                                <div>
                                    {item.dob} - CCCD: {item.cmnd}
                                </div>
                            </Space>
                        </List.Item>
                    )}
                />
            </Modal>
        </div>
    );
};
